import { defineComponent, ref, computed, toRef, watch, resolveComponent, openBlock, createBlock, withCtx, createElementVNode, mergeProps, normalizeClass, renderSlot, createTextVNode, toDisplayString, createElementBlock, createVNode, createCommentVNode } from 'vue';
import { useMagicKeys, whenever, onClickOutside } from '@vueuse/core';
import { logicAnd } from '@vueuse/math';
import { t as te } from '../OrAutocompleteV3-508d99c0.js';
import { OrOverlayV3 as script$1 } from '../or-overlay-v3/index.js';
import { OrButtonV3 as script$2, ButtonColor } from '../or-button-v3/index.js';
import { OrInputV3 as script$3 } from '../or-input-v3/index.js';
import '@onereach/styles/screens.json';
import '@onereach/styles/tailwind.config.json';
import 'lodash/get';
import 'lodash/isElement';
import '../or-error-v3/index.js';
import '../or-hint-v3/index.js';
import '../or-icon-button-v3/index.js';
import '../or-icon-v3/index.js';
import '../or-tooltip-v3/index.js';
import '../or-popover-v3/index.js';
import '@floating-ui/dom';
import '../or-bottom-sheet-v3/index.js';
import '../or-teleport-v3/index.js';
import '../or-input-box-v3/index.js';
import '../OrInlineInputV3-46210775.js';
import '../or-inline-input-box-v3/index.js';
import '../or-text-v3/index.js';
import '../or-label-v3/index.js';
import '../or-loader-v3/index.js';
import '../or-menu-item-v3/index.js';
import '../or-tag-input-v3/index.js';
import '../or-tags-v3/index.js';
import '../or-tag-v3/index.js';

var ConfirmType;
(function (ConfirmType) {
  ConfirmType["D"] = "d";
  ConfirmType["C"] = "c";
})(ConfirmType || (ConfirmType = {}));

const Confirm = [
// Position
'fixed md:static', 'left-0', 'right-0', 'bottom-0',
// Layout
'layout-column',
// Box
'w-full md:w-[320px]',
// Spacing
'px-lg', 'py-lg', 'gap-md',
// Shape
'md:rounded-md',
// Elevation
'shadow-modal', 'dark:shadow-modal-dark',
// Typography
'typography-body-2-regular',
// Theme
'theme-background-default', 'dark:theme-background-default-dark', 'theme-foreground-default', 'dark:theme-foreground-default-dark'];
const ConfirmHeader = [
// Layout
'layout-row justify-between',
// Typography
'typography-headline-3'];
const ConfirmInput = [
// Box
'w-full',
// Spacing
'mt-md'];
const ConfirmFooter = [
// Spacing
'gap-md'];
const ConfirmFooterTypes = {
  [ConfirmType.C]: [
  // Layout
  'layout-row'],
  [ConfirmType.D]: [
  // Layout
  'layout-row-reverse']
};
const ConfirmFooterButtons = [
// Layout
'!grow',
// Box
'w-auto md:w-[128px]'];

var script = defineComponent({
  name: 'OrConfirm',
  components: {
    OrOverlay: script$1,
    OrButton: script$2,
    OrInput: script$3
  },
  inheritAttrs: false,
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    titleText: {
      type: String,
      required: false,
      default: null
    },
    confirmButtonText: {
      type: String,
      default: 'Confirm'
    },
    autoCloseOnConfirm: {
      type: Boolean,
      default: true
    },
    cancelButtonText: {
      type: String,
      default: 'Cancel'
    },
    confirmType: {
      type: String,
      default: () => ConfirmType.C
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    confirmationText: {
      type: String,
      default: undefined
    },
    textConfirmationLabel: {
      type: String,
      default: undefined
    },
    textConfirmationPlaceholder: {
      type: String,
      default: undefined
    }
  },
  emits: ['update:state', 'open', 'close', 'confirm'],
  expose: ['root', 'state', 'open', 'close'],
  setup(props, context) {
    // Refs
    const root = ref();
    const confirmationInput = ref();
    const confirmationButton = ref();
    // Styles
    const rootStyles = computed(() => ['or-confirm-v3', ...Confirm]);
    const headerStyles = computed(() => [...ConfirmHeader]);
    const inputBoxStyles = computed(() => [...ConfirmInput]);
    const footerStyles = computed(() => [...ConfirmFooter, ...ConfirmFooterTypes[props.confirmType]]);
    const buttonsStyles = computed(() => [...ConfirmFooterButtons]);
    // State
    const {
      state,
      open,
      close
    } = te(toRef(props, 'isOpen'), context.emit);
    const isInputActive = ref(false);
    const confirmationValue = ref('');
    watch(state, () => {
      confirmationValue.value = '';
    });
    const {
      escape,
      enter
    } = useMagicKeys();
    const isInvalid = computed(() => {
      return !!props.confirmationText && props.confirmationText !== confirmationValue.value;
    });
    const isConfirmationButtonDisabled = computed(() => !!props.disabled || isInvalid.value);
    const confirmButtonColor = computed(() => props.confirmType === ConfirmType.C ? ButtonColor.Primary : ButtonColor.Danger);
    // Methods
    function confirm() {
      if (!props.disabled && !isInvalid.value) {
        context.emit('confirm');
      }
    }
    function onInputFocus() {
      isInputActive.value = true;
    }
    function onInputBlur() {
      isInputActive.value = false;
    }
    function onConfirmClick() {
      confirm();
      if (props.autoCloseOnConfirm) {
        close();
      }
    }
    // Events
    watch(escape, val => {
      if (val) close();
    });
    // types here are used for fixing TS2349
    watch(confirmationInput, val => {
      var _a;
      return (_a = val === null || val === void 0 ? void 0 : val.focus) === null || _a === void 0 ? void 0 : _a.call(val);
    });
    // types here are used for fixing TS2349
    watch(confirmationButton, val => {
      if (val && !props.confirmationText) {
        const button = val.root;
        button === null || button === void 0 ? void 0 : button.focus();
      }
    });
    whenever(logicAnd(enter, isInputActive), () => confirm());
    // #region Click Outside
    onClickOutside(root, close);
    // #endregion
    return {
      root,
      confirmationInput,
      confirmationButton,
      confirmationValue,
      confirmButtonColor,
      isInvalid,
      isConfirmationButtonDisabled,
      state,
      isInputActive,
      escape,
      rootStyles,
      headerStyles,
      inputBoxStyles,
      footerStyles,
      buttonsStyles,
      confirm,
      onConfirmClick,
      open,
      close,
      onInputFocus,
      onInputBlur
    };
  }
});

const _hoisted_1 = {
  class: /*#__PURE__*/normalizeClass(['grow', 'overflow-y-auto', 'overflow-x-hidden'])
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrInput = resolveComponent("OrInput");
  const _component_OrButton = resolveComponent("OrButton");
  const _component_OrOverlay = resolveComponent("OrOverlay");
  return _ctx.state === 'open' ? (openBlock(), createBlock(_component_OrOverlay, {
    key: 0,
    fullscreen: true
  }, {
    default: withCtx(() => [createElementVNode("div", mergeProps(_ctx.$attrs, {
      ref: 'root',
      class: _ctx.rootStyles,
      role: 'alertdialog'
    }), [createElementVNode("div", {
      class: normalizeClass(_ctx.headerStyles)
    }, [renderSlot(_ctx.$slots, "header", {}, () => [createTextVNode(toDisplayString(_ctx.titleText), 1 /* TEXT */)])], 2 /* CLASS */), createElementVNode("div", _hoisted_1, [renderSlot(_ctx.$slots, "default"), _ctx.confirmationText ? (openBlock(), createElementBlock("div", {
      key: 0,
      class: normalizeClass(_ctx.inputBoxStyles)
    }, [createVNode(_component_OrInput, {
      ref: 'confirmationInput',
      modelValue: _ctx.confirmationValue,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.confirmationValue = $event),
      label: _ctx.textConfirmationLabel,
      placeholder: _ctx.textConfirmationPlaceholder,
      onFocus: _ctx.onInputFocus,
      onBlur: _ctx.onInputBlur
    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "onFocus", "onBlur"])], 2 /* CLASS */)) : createCommentVNode("v-if", true)]), renderSlot(_ctx.$slots, "footer", {
      confirm: _ctx.confirm,
      cancel: _ctx.close
    }, () => [createElementVNode("div", {
      class: normalizeClass(_ctx.footerStyles)
    }, [createVNode(_component_OrButton, {
      class: normalizeClass(_ctx.buttonsStyles),
      variant: 'outlined',
      disabled: _ctx.loading,
      onClick: _cache[1] || (_cache[1] = $event => _ctx.close())
    }, {
      default: withCtx(() => [createTextVNode(toDisplayString(_ctx.cancelButtonText), 1 /* TEXT */)]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["class", "disabled"]), createVNode(_component_OrButton, {
      ref: 'confirmationButton',
      class: normalizeClass(_ctx.buttonsStyles),
      color: _ctx.confirmButtonColor,
      loading: _ctx.loading,
      disabled: _ctx.isConfirmationButtonDisabled,
      onClick: _ctx.onConfirmClick
    }, {
      default: withCtx(() => [createTextVNode(toDisplayString(_ctx.confirmButtonText), 1 /* TEXT */)]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["class", "color", "loading", "disabled", "onClick"])], 2 /* CLASS */)])], 16 /* FULL_PROPS */)]),
    _: 3 /* FORWARDED */
  })) : createCommentVNode("v-if", true);
}

script.render = render;
script.__file = "src/components/or-confirm-v3/OrConfirm.vue";

export { ConfirmType, script as OrConfirmV3 };
